<script lang="ts" setup>
import { Image } from "vant";
import { ChatSceneWithChatInfo } from "@/types/chat";
import { computed } from "vue";
import { marked } from "marked";
import { useReply } from "@/stores/reply";
import {
  createPrivateSession,
  startChatSessionByPopup,
} from "@/components/mobile/chat/chatUtils";
import { useMate } from "@/stores/mate";
import {
  getDefaultPersonaId,
  replacePlaceHolder,
} from "@/components/mobile/chat/mode/util";
import { useUI } from "@/stores/ui";
import { useGlobalUser } from "@/stores/globalUser";
import { useShortCount } from "../../../../use/common";

const props = defineProps<{
  scene: ChatSceneWithChatInfo;
}>();

const mateStore = useMate();
const userStore = useGlobalUser();

const targetMate = computed(
  () => props.scene.mate ?? mateStore.mateIdMap.get(props.scene.mateId)!
);
const sceneStateHTML = computed(() => {
  if (!props.scene.greeting) return "";
  // const stateStartIdx = props.scene.greeting.indexOf("> ");
  // if (stateStartIdx === -1) return "";
  // return marked(props.scene.greeting.slice(stateStartIdx));
  return marked(
    replacePlaceHolder(
      props.scene.greeting,
      targetMate.value?.name ?? "{{char}}",
      "{{user}}"
    )
  );
});

const replyStore = useReply();
const reply = computed(() =>
  props.scene.reply
    ? replyStore.replyEntity.getEntity(props.scene.reply.id!)
    : undefined
);

const isReplyLoading = computed(
  () =>
    replyStore.voteLoading &&
    replyStore.emojiReplyTarget?.id === reply.value?.id
);

const onStart = async () => {
  // 1. 未登录：弹窗
  if (!userStore.isLogin) {
    await startChatSessionByPopup(targetMate.value!, true, props.scene.id);
    return;
  }
  const uiStore = useUI();
  if (mateStore?.currentMate?.uuid === targetMate.value!.uuid) {
    uiStore.showAnotherMateSelector = true;
    return;
  }
  // 2. 直接创建session
  const defaultPersonaId = getDefaultPersonaId(targetMate.value!);
  await createPrivateSession(
    targetMate.value!.id!,
    targetMate.value!.uuid,
    "push",
    props.scene.id,
    defaultPersonaId
  );
};
</script>
<template>
  <div class="bg-neutral/60 p-2 flex flex-col gap-2 rounded-lg shadow">
    <div class="flex flex-col justify-between flex-grow">
      <div class="flex gap-2 h-[5.5rem]">
        <Image
          :src="scene.media?.url"
          class="shrink-0 rounded overflow-hidden pointer-events-none aspect-[3/4]"
          fit="cover"
          crossorigin="anonymous"
        />
        <div class="flex flex-col gap-1 flex-grow">
          <span class="text-xs font-semibold line-clamp-1">
            {{ scene.name }}
          </span>
          <span
            v-if="scene.tag"
            class="text-xs gap-1 flex w-full overflow-hidden"
          >
            <span
              v-for="item in scene.tag.split(',').slice(0, 3)"
              :key="item"
              class="bg-base-300 px-0.5 shrink-0 rounded"
            >
              {{ item }}
            </span>
          </span>
          <span class="text-xs line-clamp-3">
            {{ scene.intro }}
          </span>
        </div>
      </div>
      <div
        class="chat-container chat-state overflow-hidden line-clamp-[10] py-1 h-40"
        v-html="sceneStateHTML"
      />
    </div>
    <div class="flex justify-between">
      <div class="flex gap-1">
        <button
          v-if="reply"
          :disabled="isReplyLoading"
          class="btn btn-xs btn-primary font-normal rounded"
          @click.stop="replyStore.popEmoji(reply)"
        >
          <i class="iconify mingcute--thumb-up-2-line" />
          <span v-if="isReplyLoading" class="loading loading-xs" />
          <span v-else-if="reply && reply.emojiCount">
            {{ reply.emojiCount }}
          </span>
        </button>
        <div class="flex items-center tex-xs gap-1 text-base-content/50">
          <i class="iconify mingcute--chat-3-line" />
          {{ useShortCount(scene.chatMessageCount) }}
        </div>
      </div>
      <button class="btn btn-primary btn-xs rounded" @click.stop="onStart">
        {{ $t("Buttons.playScene") }}
      </button>
    </div>
  </div>
</template>
